import { useEffect, useState } from 'react';
import { useBoardBackgroundQuery } from './BoardBackgroundQuery.generated';
import { useWorkspace } from '@trello/workspaces';
import { usePreviousWhileLoading } from 'app/src/components/WorkspaceNavigation/usePreviousWhileLoading';

export interface BoardBackground {
  backgroundColor: string | undefined;
  backgroundTopColor: string;
  backgroundBrightness: string;
}

export function useBoardBackground() {
  const workspace = useWorkspace();
  const idBoard = usePreviousWhileLoading(
    workspace.idBoard,
    workspace.isLoading,
    null,
  );

  const skip = !idBoard;

  // `/1/board/{idBoard}?fields=id,prefs&operationName=BoardBackground`
  const { data, loading, error } = useBoardBackgroundQuery({
    skip,
    variables: { boardId: idBoard || '' },
  });

  const [boardBackground, setBoardBackground] = useState<BoardBackground>({
    backgroundColor: '',
    backgroundTopColor: '',
    backgroundBrightness: '',
  });

  const boardPrefs = data?.board?.prefs;

  useEffect(() => {
    const emptyBoardBackground = {
      backgroundColor: '',
      backgroundTopColor: '',
      backgroundBrightness: '',
    };

    if (!loading && !error && boardPrefs) {
      const {
        background,
        backgroundColor,
        backgroundTopColor,
        backgroundBrightness,
        backgroundImage,
      } = boardPrefs;
      // There is an issue right now when changing the board background from a solid color
      // to a custom image (via the right sidebar) where the nav background doesn't get set
      // correctly. The response from the PUT to `/1/boards/{boardId}` and the WebSocket
      // delta do not have a `backgroundColor` data attribute at all. The sync to Apollo cache
      // therefore doesn't null out that attribute and it retains the color from the previous
      // solid color selection.  The board prefs in the Apollo cache then do not accurately reflect
      // the current state of the board on the server. As a workaround for now, let's see
      // if `backgroundColor` and `backgroundImage` both exist and if they do then prioritize
      // `backgroundImage` and set `backgroundColor` to empty. This code can hopefully be removed
      // when https://trello.atlassian.net/browse/TRELP-5489 is addressed.
      let backgroundColorWorkaround;
      if (background && !background.includes('gradient') && backgroundImage) {
        // If NOT a gradient background (which has color, topColor, & image),
        // then check if there is an image and if so color shouldn't have a value.
        backgroundColorWorkaround = '';
      } else {
        backgroundColorWorkaround = backgroundColor;
      }

      if (backgroundImage && backgroundBrightness === 'unknown') {
        // It is possible for backgroundBrightness to be "unknown" in cases where the
        // preview service times out / fails. Default to a light theme so things are readable.
        setBoardBackground(emptyBoardBackground);
      } else if (backgroundImage && !backgroundTopColor) {
        // Today Unsplash is used to select an image background for a board.
        // However, before this was around there were a set of stock images
        // that you could pick from. These must have existed before the logic to
        // calculate backgroundTopColor was in place so this value is null.
        // Because of this and the fact that backgroundBrightness does have a value,
        // we need to reset so these legacy boards are usable.
        setBoardBackground(emptyBoardBackground);
      } else {
        setBoardBackground({
          backgroundColor: backgroundColorWorkaround as string | undefined,
          backgroundTopColor,
          backgroundBrightness,
        });
      }
    }

    if ((!boardPrefs && skip) || error) {
      // non-board page or error so reset
      setBoardBackground(emptyBoardBackground);
    }
  }, [loading, error, boardPrefs, skip]);

  return boardBackground;
}
